/**
 * Command Provider
 * Handles LaTeX command completions (\command)
 */

import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { EditorView } from '@codemirror/view'
import { PositionInfo, LaTeXContext, CommandCategory } from '../types'
import { CompletionProvider } from './baseProvider'
import { findCommands, getCommandMetadata, getAllCommands } from '../commandRegistry'
import { parseSnippet, insertSnippet } from '../snippetEngine'
import { rankCommands } from '../commandRanking'
import { logDebug } from '@utils/logger'

export class CommandProvider implements CompletionProvider {
  name = 'command'
  priority = 100 // High priority for command completions

  canHandle(context: CompletionContext, position: PositionInfo, latexContext: LaTeXContext): boolean {
    return position.isInCommand || position.isAfterBackslash
  }

  provide(context: CompletionContext, position: PositionInfo, latexContext: LaTeXContext): CompletionResult | null {
    const { state, pos } = context
    const text = state.doc.toString()
    
    // Find backslash position
    let backslashPos = -1
    for (let i = pos - 1; i >= 0 && (pos - i) <= 50; i--) {
      if (text[i] === '\\' && (i === 0 || text[i - 1] !== '\\')) {
        backslashPos = i
        break
      }
      if (text[i] === ' ' || text[i] === '\n' || text[i] === '\t') {
        break
      }
    }
    
    if (backslashPos < 0) return null
    
    // Extract query (text after backslash)
    const query = text.substring(backslashPos + 1, pos).toLowerCase()
    
    // Filter by context if in math mode
    const category = latexContext.inMathMode ? CommandCategory.MATH : undefined
    
    // Find matching commands
    let commandMatches = findCommands(query, category)
    
    // If query is empty, show common commands first
    if (query === '') {
      const commonCommands = getAllCommands()
        .filter(cmd => cmd.usageFrequency >= 80)
        .sort((a, b) => b.usageFrequency - a.usageFrequency)
        .slice(0, 30)
      
      const otherCommands = getAllCommands()
        .filter(cmd => cmd.usageFrequency < 80)
        .slice(0, 20)
      
      commandMatches = [...commonCommands, ...otherCommands]
    }
    
    // Rank commands by context relevance
    commandMatches = rankCommands(commandMatches, query, latexContext)
    
    if (commandMatches.length === 0) return null
    
    const capturedBackslashPos = backslashPos
    const capturedPos = pos
    
    const options = commandMatches.slice(0, 50).map(cmd => {
      const cmdName = cmd.name
      
      // Special handling for \begin - handled by multi-stage provider
      if (cmdName === 'begin') {
        return {
          label: 'begin',
          type: 'function' as const,
          info: 'Begin environment - type { and environment name',
          apply: (view: EditorView) => {
            const fullText = `\\begin{}`
            const cursorPosition = capturedBackslashPos + 7
            
            view.dispatch({
              changes: {
                from: capturedBackslashPos,
                to: capturedPos,
                insert: fullText
              },
              selection: {
                anchor: cursorPosition
              }
            })
          }
        }
      }
      
      // Use snippet if available
      if (cmd.snippet) {
        const parsed = parseSnippet(cmd.snippet)
        
        // Prepend backslash to snippet text since we're replacing from backslash position
        const snippetWithBackslash: ParsedSnippet = {
          ...parsed,
          text: `\\${parsed.text}`,
          tabStops: parsed.tabStops.map(stop => ({
            ...stop,
            start: stop.start + 1, // Shift by 1 to account for the backslash
            end: stop.end + 1
          })),
          finalPosition: parsed.finalPosition + 1
        }
        
        return {
          label: cmdName,
          type: 'function' as const,
          info: cmd.description,
          apply: (view: EditorView) => {
            // Use insertSnippet which handles tab stops automatically
            insertSnippet(view, capturedBackslashPos, capturedPos, snippetWithBackslash)
          }
        }
      }
      
      // Simple command without snippet
      return {
        label: cmdName,
        type: 'function' as const,
        info: cmd.description,
        apply: `\\${cmdName}`
      }
    })
    
    logDebug('[CommandProvider] Providing completions', {
      query,
      optionsCount: options.length,
      category
    })
    
    return {
      from: backslashPos,
      to: pos,
      options,
      filter: false,
      span: /^\\[a-zA-Z*]*$/,
      validFor: /^\\[a-zA-Z*]*$/
    } as CompletionResult & { span: RegExp; validFor: RegExp }
  }
}

